/**
 * 청크 처리 순수 함수들
 * 스토어 로직에서 분리하여 재사용 가능하고 테스트 가능하게 구성
 */

import type { ChunkMessage, TextChunkMessage } from "../types/websocket-messages";
import type { ChatMessageType } from "@/features/text-chat-with-agent/types/chat-message";
import { appendStreamingText, stripEmotionTagsForDisplay } from "@/shared/lib/emotion-tags";

export interface ChunkProcessingResult {
  chunks: ChunkMessage[];
  updatedMessages: ChatMessageType[];
  isLoading: boolean;
  shouldSetCurrentChunkToEnd: boolean;
}

export interface TextChunkProcessingResult {
  textChunks: TextChunkMessage[];
  isLoading: boolean;
}

/**
 * 음성 청크 처리 (TTS + 립싱크)
 */
export function processVoiceChunk(
  chunk: ChunkMessage,
  currentState: {
    chunks: ChunkMessage[];
    messages: ChatMessageType[];
    currentTurnId: string | null;
  }
): ChunkProcessingResult | null {
  // 턴 ID 검증
  if (!currentState.currentTurnId || chunk.turn_id !== currentState.currentTurnId) {
    console.warn(
      `🚫 turnId 불일치로 chunk 무시: current=${currentState.currentTurnId}, received=${chunk.turn_id}`
    );
    return null;
  }

  // 즉시 -1 청크가 넘어온 경우 (chunks: []로 전달되어 항상 이 경로를 탐)
  if (chunk.sequence_num === -1 && currentState.chunks.length === 0) {
    const terminalText = (chunk.full_text || chunk.text)?.trim();
    const terminalMessages = terminalText && chunk.turn_id !== "greeting_turn"
      ? updateMessagesWithFullText(
          currentState.messages,
          chunk.turn_id,
          terminalText
        )
      : currentState.messages;

    // 스트리밍 중인 메시지의 isStreaming을 false로 변경
    const finalMessages = terminalMessages.map((msg) =>
      msg.turnId === chunk.turn_id && msg.sender === "agent" && msg.isStreaming
        ? { ...msg, isStreaming: false }
        : msg
    );
    return {
      chunks: [chunk],
      updatedMessages: finalMessages,
      isLoading: false,
      shouldSetCurrentChunkToEnd: true,
    };
  }

  const newChunks = [...currentState.chunks, chunk];
  const hasLastChunk = newChunks.find((c) => c.sequence_num === -1);

  // 메시지 업데이트 로직
  let updatedMessages = currentState.messages;

  // 1. full_text가 있는 경우 (주로 마지막 청크)
  const fullText = chunk.full_text?.trim();
  if (fullText && chunk.turn_id !== "greeting_turn") {
    updatedMessages = updateMessagesWithFullText(
      currentState.messages,
      chunk.turn_id,
      fullText
    );
  }
  // 2. 청크 텍스트만 있는 경우 (중간 청크들) - 스트리밍 방식으로 처리
  else if (chunk.text?.trim() && chunk.turn_id !== "greeting_turn") {
    updatedMessages = updateMessagesWithChunkText(
      currentState.messages,
      chunk.turn_id,
      chunk.text
    );
  }

  // 마지막 청크에 대한 처리 (-1번 청크는 무조건 마지막에 들어가도록 처리)
  if (hasLastChunk) {
    // 마지막 청크일 때 스트리밍 상태를 완료로 변경
    const finalMessages = updatedMessages.map((msg) =>
      msg.turnId === chunk.turn_id && msg.sender === "agent" && msg.isStreaming
        ? { ...msg, isStreaming: false }
        : msg
    );

    return {
      chunks: [
        ...newChunks
          .filter((c) => c.sequence_num !== -1)
          .toSorted((a, b) => a.sequence_num - b.sequence_num),
        hasLastChunk,
      ],
      updatedMessages: finalMessages,
      isLoading: false,
      shouldSetCurrentChunkToEnd: false,
    };
  }

  // 중간 청크: 로딩 상태 유지 (마지막 청크가 올 때까지)
  return {
    chunks: newChunks.toSorted((a, b) => a.sequence_num - b.sequence_num),
    updatedMessages,
    isLoading: true, // 마지막 청크가 올 때까지 로딩 유지
    shouldSetCurrentChunkToEnd: false,
  };
}

/**
 * 텍스트 청크 처리 (텍스트 전용)
 */
export function processTextChunk(
  textChunk: TextChunkMessage,
  currentState: {
    textChunks: TextChunkMessage[];
    currentTurnId: string | null;
  }
): TextChunkProcessingResult | null {
  // 최신 턴 외의 텍스트는 새 응답에 이어붙이지 않는다.
  if (!currentState.currentTurnId || textChunk.turn_id !== currentState.currentTurnId) {
    console.warn("⚠️ [CHUNK_PROCESSOR] turn_id 불일치, 무시");
    return null;
  }
  const result = {
    textChunks: [...currentState.textChunks, textChunk],
    // is_final이 true일 때만 로딩 종료 (스트리밍 중에는 로딩 상태 유지)
    isLoading: !textChunk.is_final,
  };

  return result;
}

/**
 * Full text로 메시지 업데이트
 */
function updateMessagesWithFullText(
  messages: ChatMessageType[],
  turnId: string,
  fullText: string
): ChatMessageType[] {
  const displayText = stripEmotionTagsForDisplay(fullText);
  const existingMessage = messages.find(
    (msg) => msg.turnId === turnId && msg.sender === "agent"
  );

  if (!existingMessage) {
    // 새 메시지 추가
    return [
      ...messages,
      {
        sender: "agent" as const,
        message: displayText,
        rawMessage: fullText,
        turnId: turnId,
      },
    ];
  } else {
    // 기존 메시지 업데이트
    return messages.map((msg) =>
      msg.turnId === turnId && msg.sender === "agent"
        ? { ...msg, message: displayText, rawMessage: fullText }
        : msg
    );
  }
}

/**
 * 청크 텍스트로 메시지 스트리밍 업데이트
 */
function updateMessagesWithChunkText(
  messages: ChatMessageType[],
  turnId: string,
  chunkText: string
): ChatMessageType[] {
  const existingMessage = messages.find(
    (msg) => msg.turnId === turnId && msg.sender === "agent"
  );

  if (!existingMessage) {
    // 첫 번째 청크 - 새 메시지 시작
    return [
      ...messages,
      {
        sender: "agent" as const,
        message: stripEmotionTagsForDisplay(chunkText),
        rawMessage: chunkText,
        turnId: turnId,
        isStreaming: true, // 스트리밍 중임을 표시
      },
    ];
  } else if (existingMessage.isStreaming) {
    // 정제 전 원문을 누적한 뒤 전체를 다시 정제해야 청크 경계의 태그/괄호를 보존할 수 있다.
    const rawMessage = appendStreamingText(
      existingMessage.rawMessage ?? existingMessage.message ?? "",
      chunkText
    );
    const accumulatedText = stripEmotionTagsForDisplay(rawMessage);
    return messages.map((msg) =>
      msg.turnId === turnId && msg.sender === "agent"
        ? { ...msg, message: accumulatedText, rawMessage }
        : msg
    );
  }

  return messages;
}

/**
 * 청크 텍스트 추출 (toast 표시용)
 */
export function extractChunkText(chunk: ChunkMessage): string | null {
  return chunk.text ? stripEmotionTagsForDisplay(chunk.text) || null : null;
}

/**
 * 청크 duration 계산 (toast duration용)
 */
export function calculateChunkDuration(chunk: ChunkMessage): number {
  const lastFrameIdx = chunk.lipsync?.last_frame_idx ?? 0;
  return ((lastFrameIdx / 30) * 1.1 * 1000);
}
